import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlarmClock, AlertTriangle, ArrowLeft, ArrowRight, Camera, Check, CheckCircle2,
  ClipboardList, Handshake, Hourglass, MessageSquare, Mic, Monitor, Timer,
  Upload, Video, VolumeX, XCircle,
} from 'lucide-react';
import { detectAdvanceCue } from '../advanceCues';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const SILENCE_MS = 8000;
const MIN_ANSWER_WORDS = 8;

function isSkipUtterance(text) {
  return detectAdvanceCue(text) === 'skip';
}

function isDoneUtterance(text) {
  return detectAdvanceCue(text) === 'next';
}

function wordCount(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

function loadSavedInterviewState(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

// ── Canvas waveform ring ──────────────────────────────────────────────────────
function WaveformRing({ speaking, thinking, size }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const levelsRef = useRef(new Float32Array(64).fill(2));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W   = canvas.width;
    const H   = canvas.height;
    const cx  = W / 2;
    const cy  = H / 2;
    const R   = size / 2 + 14;   // just outside the photo circle
    const N   = 64;               // number of bars

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < N; i++) {
        // Target: tall + noisy when speaking, just a tiny stub otherwise
        const target = speaking
          ? 4 + Math.abs(Math.sin(Date.now() * 0.003 + i * 0.4)) * 22 + Math.random() * 8
          : thinking
            ? 2 + Math.abs(Math.sin(Date.now() * 0.002 + i * 0.2)) * 5
            : 2.5;

        // Smooth lerp toward target
        levelsRef.current[i] += (target - levelsRef.current[i]) * 0.22;
        const h = levelsRef.current[i];

        const angle = (i / N) * Math.PI * 2 - Math.PI / 2;
        const x1 = cx + R * Math.cos(angle);
        const y1 = cy + R * Math.sin(angle);
        const x2 = cx + (R + h) * Math.cos(angle);
        const y2 = cy + (R + h) * Math.sin(angle);

        // Colour: sweeps from violet → indigo → blue → indigo as we go around
        const hue = 240 + Math.sin((i / N) * Math.PI * 2) * 30;
        const alpha = speaking ? 0.85 : thinking ? 0.5 : 0.25;
        ctx.strokeStyle = `hsla(${hue}, 85%, 72%, ${alpha})`;
        ctx.lineWidth   = 2.2;
        ctx.lineCap     = 'round';
        ctx.shadowBlur  = speaking ? 6 : 0;
        ctx.shadowColor = `hsla(${hue}, 85%, 72%, 0.5)`;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [speaking, thinking, size]);

  const pad = 60; // canvas extends beyond the photo to fit the bars
  return (
    <canvas
      ref={canvasRef}
      width={size + pad * 2}
      height={size + pad * 2}
      style={{ position: 'absolute', pointerEvents: 'none' }}
    />
  );
}

// ── AI Avatar ─────────────────────────────────────────────────────────────────
function AiAvatar({ speaking, thinking, size = 156, photoUrl, name = 'Alex' }) {

  if (photoUrl) {
    const pad = 60;
    return (
      <div style={{
        position: 'relative',
        width: size + pad * 2, height: size + pad * 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>

        {/* Ambient glow blob behind the face */}
        <div style={{
          position: 'absolute', borderRadius: '50%',
          width: size + 40, height: size + 40,
          background: speaking
            ? 'radial-gradient(circle, rgba(99,102,241,0.30) 0%, rgba(139,92,246,0.12) 50%, transparent 70%)'
            : 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 65%)',
          filter: 'blur(12px)',
          transition: 'background 0.6s',
        }} />

        {/* Canvas waveform — the cinematic ring */}
        <WaveformRing speaking={speaking} thinking={thinking} size={size} />

        {/* Photo circle */}
        <div style={{
          position: 'relative', width: size, height: size, flexShrink: 0,
          borderRadius: '50%', overflow: 'hidden',
          border: `2.5px solid ${speaking ? 'rgba(139,92,246,0.9)' : 'rgba(99,102,241,0.35)'}`,
          boxShadow: speaking
            ? '0 0 0 4px rgba(99,102,241,0.15), 0 0 40px rgba(99,102,241,0.35)'
            : '0 0 0 2px rgba(99,102,241,0.08), 0 0 20px rgba(99,102,241,0.15)',
          transition: 'border-color 0.4s, box-shadow 0.4s',
          zIndex: 1,
        }}>
          {/* Natural photo — only very subtle warm/cool shift on state */}
          <img src={photoUrl} alt={name} style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: speaking
              ? 'brightness(1.05) contrast(1.04) saturate(1.08)'
              : 'brightness(0.96) contrast(1.0) saturate(0.95)',
            transition: 'filter 0.5s',
          }} />

          {/* Subtle vignette to blend into dark bg */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle at 50% 50%, transparent 55%, rgba(8,14,28,0.45) 100%)',
          }} />
        </div>

        {/* Thinking dots — float below the circle */}
        {thinking && !speaking && (
          <div style={{
            position: 'absolute',
            bottom: pad / 2 - 10,
            left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 5,
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 7, height: 7, borderRadius: '50%',
                background: 'rgba(139,92,246,0.8)',
                animation: 'thinkDot 0.8s ease-in-out infinite',
                animationDelay: `${i * 0.18}s`,
              }} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Fallback: animated ⬡ logo avatar ────────────────────────────────────────
  const logoBars = [2, 4, 6, 9, 11, 9, 6, 4, 6, 9, 6, 4, 2];
  const pad = 60;
  return (
    <div style={{
      position: 'relative',
      width: size + pad * 2, height: size + pad * 2,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        position: 'absolute', borderRadius: '50%',
        width: size + 40, height: size + 40,
        background: speaking
          ? 'radial-gradient(circle, rgba(99,102,241,0.30) 0%, rgba(139,92,246,0.12) 50%, transparent 70%)'
          : 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 65%)',
        filter: 'blur(12px)',
        transition: 'background 0.6s',
      }} />
      <WaveformRing speaking={speaking} thinking={thinking} size={size} />
      <div style={{
        width: size, height: size, borderRadius: '50%', zIndex: 1,
        background: 'linear-gradient(140deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)',
        border: `2.5px solid ${speaking ? 'rgba(139,92,246,0.9)' : 'rgba(99,102,241,0.35)'}`,
        boxShadow: speaking
          ? '0 0 0 4px rgba(99,102,241,0.15), 0 0 40px rgba(99,102,241,0.35)'
          : '0 0 0 2px rgba(99,102,241,0.08), 0 0 20px rgba(99,102,241,0.15)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden', flexShrink: 0,
        transition: 'border-color 0.4s, box-shadow 0.4s',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, transparent 55%, rgba(8,14,28,0.45) 100%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ fontSize: size * 0.3, lineHeight: 1, filter: 'drop-shadow(0 0 10px rgba(165,180,252,0.7))' }}>⬡</div>
        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 22, marginTop: 10 }}>
          {logoBars.map((h, i) => (
            <div key={i} style={{
              width: 3, borderRadius: 2,
              background: speaking ? 'rgba(165,180,252,0.9)' : 'rgba(165,180,252,0.3)',
              height: speaking ? undefined : Math.max(3, h * 0.4),
              minHeight: 3, maxHeight: 20,
              animation: speaking ? `eq${i % 4} ${0.3 + (i % 3) * 0.1}s ease-in-out infinite alternate` : 'none',
              animationDelay: `${i * 0.05}s`,
            }} />
          ))}
        </div>
      </div>
      {thinking && !speaking && (
        <div style={{ position: 'absolute', bottom: pad / 2 - 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(139,92,246,0.8)', animation: 'thinkDot 0.8s ease-in-out infinite', animationDelay: `${i * 0.18}s` }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Mic level visualizer ──────────────────────────────────────────────────────
function MicWave({ level, active }) {
  const bars = 16;
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 28 }}>
      {Array.from({ length: bars }).map((_, i) => {
        const center = Math.abs(i - bars / 2) / (bars / 2);
        const h = active ? Math.max(3, (level / 100) * 24 * (1 - center * 0.5) * (0.6 + Math.random() * 0.4)) : 3;
        return (
          <div key={i} style={{
            width: 3, borderRadius: 2, transition: 'height 0.05s',
            background: active ? '#22c55e' : 'rgba(100,116,139,0.4)',
            height: h,
          }} />
        );
      })}
    </div>
  );
}

export default function PortalInterview() {
  const { token } = useParams();
  const navigate = useNavigate();
  const storageKey = `portal-interview:${token}`;

  const [phase, setPhase] = useState('loading');
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQIdx, setCurrentQIdx] = useState(0);
  // Adaptive difficulty
  const [questionPool, setQuestionPool] = useState({ easy: [], medium: [], hard: [] });
  const [usedTechIds, setUsedTechIds] = useState(new Set());
  const [currentDifficulty, setCurrentDifficulty] = useState('medium');
  // Per-question states & countdown timer
  const [questionStates, setQuestionStates] = useState([]); // 'pending'|'answered'|'skipped' per question index
  const [questionTimer, setQuestionTimer] = useState(120);
  const [questionTimerMax, setQuestionTimerMax] = useState(120);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const [stream, setStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [proctoring, setProctoring] = useState({ faceDetected: true, screenshots: [], headFlags: [], lastScreenshot: null });
  const [score, setScore] = useState({ total: 0, breakdown: [] });
  const [elapsed, setElapsed] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [resumePending, setResumePending] = useState(false);
  const [aiStatus, setAiStatus] = useState('waiting'); // 'speaking' | 'thinking' | 'listening' | 'waiting'

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micLevelAnimRef = useRef(null);
  const chunksRef = useRef([]);
  const screenChunksRef = useRef([]);
  const screenshotIntervalRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const questionTimerRef = useRef(null);     // tracks question start time (for time_taken calc)
  const questionCountdownRef = useRef(null); // setInterval for per-question countdown
  const submitAnswerRef = useRef(null);      // kept current to avoid stale closure in countdown
  const skipQuestionRef = useRef(null);
  const autoSubmitFiredRef = useRef(false);  // prevents double-fire when timer hits 0
  const countdownArmedRef = useRef(false);   // true only after AI finished speaking this question
  const restoredStateRef = useRef(null);
  const interviewBootstrappedRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const listenWantedRef = useRef(false);
  const transcriptRef = useRef('');
  const spokenFinalsRef = useRef('');
  const isAiSpeakingRef = useRef(false);
  const loadingRef = useRef(false);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { isAiSpeakingRef.current = isAiSpeaking; }, [isAiSpeaking]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const cameraActive = Boolean(stream?.getVideoTracks?.().some(t => t.readyState === 'live' && t.enabled));
  const micActive = Boolean(stream?.getAudioTracks?.().some(t => t.readyState === 'live' && t.enabled));

  // Sync speaking state
  useEffect(() => {
    if (isAiSpeaking) setAiStatus('speaking');
    else if (loading) setAiStatus('thinking');
    else if (isRecording) setAiStatus('listening');
    else setAiStatus('waiting');
  }, [isAiSpeaking, loading, isRecording]);

  useEffect(() => {
    if (!videoRef.current || !stream) return;
    videoRef.current.srcObject = stream;
    videoRef.current.play?.().catch(() => {});
  }, [stream, phase]);

  useEffect(() => {
    if (phase !== 'interviewing' && phase !== 'uploading') return undefined;
    const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [phase]);

  useEffect(() => { streamRef.current = stream; }, [stream]);
  useEffect(() => { screenStreamRef.current = screenStream; }, [screenStream]);

  useEffect(() => {
    if (!session?.session_id) return;
    if (!['permissions', 'interviewing', 'uploading', 'results'].includes(phase)) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        session_id: session.session_id, phase, messages, currentQIdx,
        transcript, score, elapsed, proctoring, results, questionStates,
        verified: !['loading', 'verify', 'expired'].includes(phase),
      }));
    } catch (_) {}
  }, [session?.session_id, phase, messages, currentQIdx, transcript, score, elapsed, proctoring, results, questionStates, storageKey]);

  useEffect(() => {
    const saved = loadSavedInterviewState(storageKey);
    fetch(`${API}/interview/${token}`)
      .then(r => { if (!r.ok) throw new Error('Session not found'); return r.json(); })
      .then(data => {
        setSession(data);
        setQuestions(data.questions || []);
        setQuestionStates((data.questions || []).map(() => 'pending'));
        // Set up adaptive pool and mark seeded tech questions as used
        if (data.question_pool) {
          setQuestionPool(data.question_pool);
          const seededIds = new Set(
            (data.questions || []).filter(q => q.type === 'technical').map(q => q.id)
          );
          setUsedTechIds(seededIds);
        }
        // If link is expired (and not already in progress), show expired screen
        if (data.is_expired) { setPhase('expired'); return; }

        if (saved && saved.session_id === data.session_id) {
          restoredStateRef.current = saved;
          setMessages(saved.messages || []);
          setCurrentQIdx(saved.currentQIdx || 0);
          setTranscript(saved.transcript || '');
          setScore(saved.score || { total: 0, breakdown: [] });
          setElapsed(saved.elapsed || 0);
          setProctoring(saved.proctoring || { faceDetected: true, screenshots: [], headFlags: [], lastScreenshot: null });
          setResults(saved.results || null);
          setQuestionStates(saved.questionStates || (data.questions || []).map(() => 'pending'));
          if (saved.phase === 'results' && saved.results) { setPhase('results'); setResumePending(false); }
          else if (['interviewing', 'uploading'].includes(saved.phase)) { setPhase('permissions'); setResumePending(true); }
          // Already verified in a previous tab session — skip verify
          else if (saved.verified) setPhase(saved.phase || 'rules');
          else setPhase('verify');
        } else {
          // New session — must verify identity first
          setPhase(data.status === 'In Progress' || data.status === 'Completed' ? 'rules' : 'verify');
        }
      })
      .catch(e => { setError(e.message || 'Failed to load interview.'); setPhase('loading'); });
  }, [token, storageKey]);

  useEffect(() => {
    return () => {
      clearInterval(screenshotIntervalRef.current);
      clearInterval(elapsedTimerRef.current);
      clearInterval(questionCountdownRef.current);
      cancelAnimationFrame(micLevelAnimRef.current);
      recognitionRef.current?.stop();
      window.speechSynthesis?.cancel();
      if (audioContextRef.current?.state !== 'closed') audioContextRef.current?.close();
      streamRef.current?.getTracks?.().forEach(t => t.stop());
      screenStreamRef.current?.getTracks?.().forEach(t => t.stop());
    };
  }, []);

  const setupMicLevel = useCallback((s) => {
    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(s).connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      const tick = () => {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        setMicLevel(Math.min(100, (data.reduce((a, b) => a + b, 0) / data.length) * 2));
        micLevelAnimRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { console.warn('Mic level setup failed:', e); }
  }, []);

  async function startSessionRequest() {
    await fetch(`${API}/interview/${token}/start`, { method: 'POST' });
  }

  const setupMedia = useCallback(async () => {
    try {
      const camStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(camStream);
      setupMicLevel(camStream);
      const scrStream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: 'screen' }, audio: true });
      setScreenStream(scrStream);
      setPhase('interviewing');
      if (!resumePending) await startSessionRequest();
      interviewBootstrappedRef.current = false;
      setResumePending(false);
    } catch (e) {
      alert('Please allow camera, microphone, and screen sharing to proceed.');
    }
  }, [setupMicLevel, resumePending, token]);

  const startRecordings = useCallback((camStream, scrStream) => {
    if (!camStream) return;
    try {
      const mr = new MediaRecorder(camStream, { mimeType: 'video/webm' });
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(500);
      mediaRecorderRef.current = mr;
    } catch (e) { console.warn('Camera recording failed:', e); }
    if (scrStream) {
      try {
        const smr = new MediaRecorder(scrStream, { mimeType: 'video/webm' });
        smr.ondataavailable = e => { if (e.data.size > 0) screenChunksRef.current.push(e.data); };
        smr.start(500);
        screenRecorderRef.current = smr;
      } catch (e) { console.warn('Screen recording failed:', e); }
    }
  }, []);

  const startScreenshotInterval = useCallback(() => {
    screenshotIntervalRef.current = setInterval(async () => {
      if (!videoRef.current?.videoWidth) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth || 320;
        canvas.height = videoRef.current.videoHeight || 240;
        canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
        let headDetected = true;
        try {
          if ('FaceDetector' in window) {
            const faces = await new window.FaceDetector().detect(canvas);
            headDetected = faces.length > 0;
            setProctoring(p => ({ ...p, faceDetected: headDetected, headFlags: headDetected ? p.headFlags : [...p.headFlags, { timestamp: new Date().toISOString(), event: 'face_not_detected' }] }));
          }
        } catch (_) {}
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const fd = new FormData();
          fd.append('screenshot', blob, `${Date.now()}.jpg`);
          fd.append('timestamp', new Date().toISOString());
          fd.append('head_detected', headDetected.toString());
          try { await fetch(`${API}/interview/${token}/screenshot`, { method: 'POST', body: fd }); } catch (_) {}
          setProctoring(p => ({ ...p, screenshots: [...p.screenshots, { timestamp: Date.now(), head_detected: headDetected }] }));
        }, 'image/jpeg', 0.7);
      } catch (e) { console.warn('Screenshot failed:', e); }
    }, 30000);
  }, [token]);

  const startElapsedTimer = useCallback(() => {
    elapsedTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  }, []);

  const speakText = useCallback((text, { rate = 1, pitch = 1 } = {}) => {
    if (!text) return Promise.resolve();
    return new Promise((resolve) => {
      try {
        recognitionRef.current?.stop();
        setIsRecording(false);
        setIsAiSpeaking(true);
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = rate;
        utter.pitch = pitch;
        utter.onend = () => { setIsAiSpeaking(false); resolve(); };
        utter.onerror = () => { setIsAiSpeaking(false); resolve(); };
        window.speechSynthesis.speak(utter);
      } catch (e) { setIsAiSpeaking(false); resolve(); }
    });
  }, []);

  const speakQuestion = useCallback((text) => speakText(text, { rate: 0.9, pitch: 1 }), [speakText]);
  const speakResponse = useCallback((text) => speakText(text, { rate: 1.0, pitch: 1 }), [speakText]);

  const startListening = useCallback(() => {
    if (isAiSpeakingRef.current || loadingRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    listenWantedRef.current = true;
    try {
      const previous = recognitionRef.current;
      recognitionRef.current = null;
      try { previous?.stop(); } catch (_) { /* ignore */ }
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e) => {
        let interim = '';
        let latestFinal = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const piece = e.results[i][0].transcript;
          if (e.results[i].isFinal) {
            latestFinal += `${piece} `;
            spokenFinalsRef.current = `${spokenFinalsRef.current} ${piece}`.replace(/\s+/g, ' ').trim();
          } else {
            interim += piece;
          }
        }
        if (isSkipUtterance(latestFinal) || isSkipUtterance(interim) || isSkipUtterance(`${latestFinal} ${interim}`)) {
          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          skipQuestionRef.current?.();
          return;
        }
        if (isDoneUtterance(latestFinal) || isDoneUtterance(interim) || isDoneUtterance(`${latestFinal} ${interim}`)) {
          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          submitAnswerRef.current?.();
          return;
        }
        const live = `${spokenFinalsRef.current}${interim ? ` ${interim}` : ''}`.replace(/\s+/g, ' ').trim();
        setTranscript(live);
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        if (!interim.trim()) {
          silenceTimerRef.current = setTimeout(() => {
            const spoken = (transcriptRef.current || '').trim();
            if (
              wordCount(spoken) >= MIN_ANSWER_WORDS
              && !isAiSpeakingRef.current
              && !loadingRef.current
            ) {
              submitAnswerRef.current?.();
            }
          }, SILENCE_MS);
        }
      };
      rec.onerror = () => {
        if (recognitionRef.current === rec) setIsRecording(false);
      };
      rec.onend = () => {
        if (recognitionRef.current !== rec) return;
        recognitionRef.current = null;
        setIsRecording(false);
        if (listenWantedRef.current && !isAiSpeakingRef.current && !loadingRef.current) {
          setTimeout(() => {
            if (listenWantedRef.current && !isAiSpeakingRef.current && !loadingRef.current) {
              startListening();
            }
          }, 250);
        }
      };
      rec.start();
      recognitionRef.current = rec;
      setIsRecording(true);
    } catch (e) { console.warn('Speech recognition failed:', e); }
  }, []);

  const stopListening = useCallback(() => {
    listenWantedRef.current = false;
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  // ── Adaptive difficulty: pick next tech question based on running performance ──
  const pickNextTechQuestion = useCallback((updatedScore) => {
    const techBreakdown = (updatedScore.breakdown || []).filter(b => b.type === 'technical');
    const perf = techBreakdown.length > 0
      ? techBreakdown.reduce((sum, b) => sum + (b.score || 0) / Math.max(b.max_marks || 1, 1), 0) / techBreakdown.length
      : 0.5;

    // Determine target difficulty based on average tech performance
    const targetDiff = perf >= 0.65 ? 'hard' : perf <= 0.35 ? 'easy' : 'medium';

    // Try target pool first, then fall back to adjacent difficulties
    const order = targetDiff === 'hard' ? ['hard', 'medium', 'easy']
                : targetDiff === 'easy' ? ['easy', 'medium', 'hard']
                : ['medium', 'easy', 'hard'];

    for (const diff of order) {
      const available = (questionPool[diff] || []).filter(q => !usedTechIds.has(q.id));
      if (available.length > 0) {
        return { q: available[0], difficulty: diff };
      }
    }
    return null; // pool exhausted
  }, [questionPool, usedTechIds]);

  // ── Per-question timer ────────────────────────────────────────────────────────
  const calcQuestionTimer = (q) => {
    if (!q) return 120;
    const text = (q.question || q.content || '').trim();
    const words = text ? text.split(/\s+/).length : 0;
    let secs = Math.max(60, Math.min(240, 60 + words * 4));
    if (q.type === 'technical') {
      const diff = (q.difficulty || 'Intermediate').toLowerCase();
      if (diff === 'advanced') secs = Math.min(240, secs + 60);
      else if (diff === 'basic') secs = Math.max(60, secs - 10);
      else secs = Math.min(240, secs + 30);
    }
    return secs;
  };

  // Reset the timer display when the question changes — countdown starts only after AI finishes speaking
  useEffect(() => {
    if (phase !== 'interviewing') return;
    const q = questions[currentQIdx];
    if (!q) return;
    clearInterval(questionCountdownRef.current);
    autoSubmitFiredRef.current = false; // allow auto-submit for the new question
    countdownArmedRef.current = false;
    const duration = calcQuestionTimer(q);
    setQuestionTimer(duration);
    setQuestionTimerMax(duration);
    return () => clearInterval(questionCountdownRef.current);
  }, [currentQIdx, questions.length, phase]); // eslint-disable-line

  // Call this right after speakQuestion() returns to begin the countdown
  const startQuestionCountdown = useCallback(() => {
    clearInterval(questionCountdownRef.current);
    countdownArmedRef.current = true;
    questionCountdownRef.current = setInterval(() => {
      setQuestionTimer(prev => {
        if (prev <= 1) {
          clearInterval(questionCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Auto-submit when timer reaches 0 — must be a useEffect, not inside the state setter
  useEffect(() => {
    if (questionTimer !== 0 || phase !== 'interviewing' || autoSubmitFiredRef.current) return;
    if (!countdownArmedRef.current) return;
    const elapsedMs = Date.now() - (questionTimerRef.current || Date.now());
    if (elapsedMs < 15000) return;
    autoSubmitFiredRef.current = true;
    recognitionRef.current?.stop();
    setIsRecording(false);
    submitAnswerRef.current?.();
  }, [questionTimer, phase]);

  useEffect(() => {
    if (phase !== 'interviewing' || questions.length === 0 || !stream) return;
    if (interviewBootstrappedRef.current) return;
    interviewBootstrappedRef.current = true;
    startRecordings(stream, screenStream);
    startScreenshotInterval();
    startElapsedTimer();

    if (messages.length > 0) {
      questionTimerRef.current = Date.now();
      if (!transcript.trim()) setTimeout(() => startListening(), 300);
      startQuestionCountdown(); // resuming mid-session — start timer immediately
      return;
    }

    const firstQ = questions[0];
    const openingMsg = `Hello ${session?.candidate_name || 'there'}! Welcome to your interview for the ${session?.job_title} position. I'm ${session?.interviewer_name || 'Alex'}, your AI interviewer. Let's get started!`;
    setMessages([
      { role: 'ai', content: openingMsg },
      { role: 'ai', content: firstQ.question, isQuestion: true, qIdx: 0 },
    ]);
    questionTimerRef.current = Date.now();
    (async () => {
      await wait(500);
      await speakQuestion(openingMsg);
      await wait(400);
      await speakQuestion(firstQ.question);
      startQuestionCountdown(); // timer starts only after AI finishes reading the question
      startListening();
    })();
  }, [phase, questions, stream, screenStream, messages, transcript, session?.job_title, session?.candidate_name, startListening, startRecordings, startScreenshotInterval, startElapsedTimer, speakQuestion, startQuestionCountdown]);

  const submitAnswer = useCallback(async () => {
    clearInterval(questionCountdownRef.current);
    stopListening();
    const currentQ = questions[currentQIdx];
    if (!currentQ) return;
    const timeTaken = Math.round((Date.now() - (questionTimerRef.current || Date.now())) / 1000);
    const answerText = transcript.trim() || '(No answer provided)';
    const isSkipped = answerText === '(No answer provided)';

    setTranscript('');
    spokenFinalsRef.current = '';
    setQuestionStates(prev => {
      const s = [...prev];
      s[currentQIdx] = isSkipped ? 'skipped' : 'answered';
      return s;
    });
    setMessages(prev => [...prev, { role: 'candidate', content: answerText }]);

    // ── Fire-and-forget: save answer to backend, score happens in background ──
    fetch(`${API}/interview/${token}/answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question_id: currentQ.id, question: currentQ.question,
        answer_text: answerText, max_marks: currentQ.max_marks,
        expected_keywords: currentQ.keywords || [],
        time_taken_seconds: timeTaken, question_type: currentQ.type,
        is_skipped: isSkipped,
      }),
    }).catch(console.error);

    // Capture score snapshot for adaptive picking (use 0 for new entry since async)
    let capturedScore;
    setScore(prev => {
      capturedScore = {
        total: prev.total,
        breakdown: [...prev.breakdown, {
          question_id: currentQ.id, score: 0,
          max_marks: currentQ.max_marks, type: currentQ.type,
        }],
      };
      return capturedScore;
    });

    // Short AI acknowledgment — no waiting for scoring
    const ack = isSkipped ? "No problem." : "Got it, moving on.";
    setMessages(prev => [...prev, { role: 'ai', content: ack }]);
    await speakResponse(ack);
    await wait(300);

    const nextIdx = currentQIdx + 1;
    if (nextIdx < questions.length) {
      setCurrentQIdx(nextIdx);
      const nextQ = questions[nextIdx];
      questionTimerRef.current = Date.now();
      setMessages(prev => [...prev, { role: 'ai', content: nextQ.question, isQuestion: true, qIdx: nextIdx }]);
      await speakQuestion(nextQ.question);
      startQuestionCountdown();
      startListening();
    } else {
      const result = pickNextTechQuestion(capturedScore || { total: 0, breakdown: [] });
      if (result) {
        setUsedTechIds(prev => new Set([...prev, result.q.id]));
        setCurrentDifficulty(result.difficulty);
        setQuestions(prev => [...prev, result.q]);
        setQuestionStates(prev => [...prev, 'pending']);
        setCurrentQIdx(nextIdx);
        questionTimerRef.current = Date.now();
        setMessages(prev => [...prev, { role: 'ai', content: result.q.question, isQuestion: true, qIdx: nextIdx, difficulty: result.q.difficulty }]);
        await speakQuestion(result.q.question);
        startQuestionCountdown();
        startListening();
      } else {
        completeInterview();
      }
    }
  }, [questions, currentQIdx, transcript, token, currentDifficulty, pickNextTechQuestion, stopListening, startListening, speakResponse, speakQuestion, startQuestionCountdown]);

  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
  }, [submitAnswer]);

  const skipQuestion = useCallback(async () => {
    // Immediately stop AI speaking and timer
    window.speechSynthesis?.cancel();
    setIsAiSpeaking(false);
    clearInterval(questionCountdownRef.current);
    stopListening();
    setTranscript('');
    spokenFinalsRef.current = '';

    // Save skip to backend (fire-and-forget)
    const currentQ = questions[currentQIdx];
    if (currentQ) {
      fetch(`${API}/interview/${token}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: currentQ.id, question: currentQ.question,
          answer_text: '(Skipped)', max_marks: currentQ.max_marks,
          expected_keywords: [], time_taken_seconds: 0,
          question_type: currentQ.type, is_skipped: true,
        }),
      }).catch(console.error);
    }

    setQuestionStates(prev => { const s = [...prev]; s[currentQIdx] = 'skipped'; return s; });
    setMessages(prev => [...prev, { role: 'candidate', content: '(Skipped)' }]);

    const nextIdx = currentQIdx + 1;
    if (nextIdx < questions.length) {
      setCurrentQIdx(nextIdx);
      const nextQ = questions[nextIdx];
      questionTimerRef.current = Date.now();
      setMessages(prev => [...prev, { role: 'ai', content: nextQ.question, isQuestion: true, qIdx: nextIdx }]);
      await wait(200);
      await speakQuestion(nextQ.question);
      startQuestionCountdown();
      startListening();
    } else {
      // Adaptive: pick next tech question
      let currentScoreSnapshot;
      setScore(s => { currentScoreSnapshot = s; return s; });
      const result = pickNextTechQuestion(currentScoreSnapshot || { total: 0, breakdown: [] });
      if (result) {
        setUsedTechIds(prev => new Set([...prev, result.q.id]));
        setCurrentDifficulty(result.difficulty);
        setQuestions(prev => [...prev, result.q]);
        setQuestionStates(prev => [...prev, 'pending']);
        setCurrentQIdx(nextIdx);
        questionTimerRef.current = Date.now();
        setMessages(prev => [...prev, { role: 'ai', content: result.q.question, isQuestion: true, qIdx: nextIdx, difficulty: result.q.difficulty }]);
        await wait(200);
        await speakQuestion(result.q.question);
        startQuestionCountdown();
        startListening();
      } else {
        // All done — silence AI
        window.speechSynthesis?.cancel();
        setIsAiSpeaking(false);
        completeInterview();
      }
    }
  }, [questions, currentQIdx, token, pickNextTechQuestion, stopListening, startListening, speakQuestion, startQuestionCountdown]);

  useEffect(() => {
    skipQuestionRef.current = skipQuestion;
  }, [skipQuestion]);

  const stopAndUploadRecordings = useCallback(async () => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      await new Promise(resolve => { mediaRecorderRef.current.onstop = resolve; mediaRecorderRef.current.stop(); }).catch(() => {});
    }
    if (chunksRef.current.length > 0) {
      const camBlob = new Blob(chunksRef.current, { type: 'video/webm' });
      if (camBlob.size > 0) {
        const fd = new FormData(); fd.append('file', camBlob, 'camera.webm'); fd.append('recording_type', 'camera');
        await fetch(`${API}/interview/${token}/upload-recording`, { method: 'POST', body: fd }).catch(() => {});
      }
    }
    if (screenRecorderRef.current?.state !== 'inactive') {
      await new Promise(resolve => { screenRecorderRef.current.onstop = resolve; screenRecorderRef.current.stop(); }).catch(() => {});
    }
    if (screenChunksRef.current.length > 0) {
      const screenBlob = new Blob(screenChunksRef.current, { type: 'video/webm' });
      if (screenBlob.size > 0) {
        const fd = new FormData(); fd.append('file', screenBlob, 'screen.webm'); fd.append('recording_type', 'screen');
        await fetch(`${API}/interview/${token}/upload-recording`, { method: 'POST', body: fd }).catch(() => {});
      }
    }
    stream?.getTracks().forEach(t => t.stop());
    screenStream?.getTracks().forEach(t => t.stop());
  }, [token, stream, screenStream]);

  const completeInterview = useCallback(async () => {
    setPhase('uploading');
    clearInterval(screenshotIntervalRef.current);
    clearInterval(elapsedTimerRef.current);
    clearInterval(questionCountdownRef.current);
    cancelAnimationFrame(micLevelAnimRef.current);
    window.speechSynthesis?.cancel();
    recognitionRef.current?.stop();
    await stopAndUploadRecordings();
    try {
      const res = await fetch(`${API}/interview/${token}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      try { sessionStorage.removeItem(storageKey); } catch (_) {}
      setResults(data);
      setPhase('results');
    } catch (e) {
      setError('Failed to complete interview. Please contact support.');
    }
  }, [token, stopAndUploadRecordings, storageKey]);

  // ═══════════════════════════════════════════
  //   RENDER
  // ═══════════════════════════════════════════

  const styles = `
    @keyframes eq0 { from { height: 2px; } to { height: 18px; } }
    @keyframes eq1 { from { height: 4px; } to { height: 13px; } }
    @keyframes eq2 { from { height: 2px; } to { height: 20px; } }
    @keyframes eq3 { from { height: 6px; } to { height: 11px; } }
    @keyframes thinkDot { 0%,100% { transform: translateY(0); opacity: 0.4; } 50% { transform: translateY(-6px); opacity: 1; } }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes slideUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  `;

  // ── Expired ──
  if (phase === 'expired') return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', padding: 24 }}>
      <style>{styles}</style>
      <div style={{ background: '#0f172a', borderRadius: 20, border: '1px solid rgba(239,68,68,0.25)', padding: '40px 36px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', color: '#f87171' }}><AlarmClock size={48} strokeWidth={1.5} /></div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#f87171', marginBottom: 8 }}>Interview Link Expired</h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
          This interview link was valid for <strong style={{ color: '#f1f5f9' }}>12 hours</strong> from the time it was sent.<br />
          Your link has now expired.
        </p>
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#d97706', lineHeight: 1.6 }}>
          Please contact the recruitment team to request a new interview link.
        </div>
        <button onClick={() => navigate('/portal')} style={{ padding: '10px 24px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 14, cursor: 'pointer', color: '#94a3b8' }}>
          <ArrowLeft size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Browse Jobs
        </button>
      </div>
    </div>
  );

  // ── Verify Identity ──
  if (phase === 'verify') {
    const handleVerify = async (e) => {
      e.preventDefault();
      const email = e.target.email.value.trim();
      const password = e.target.password.value.trim();
      if (!email || !password) return;
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API}/interview/${token}/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        if (res.status === 410) { setPhase('expired'); return; }
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail || 'Invalid email or access password. Please check your email.');
          return;
        }
        setPhase('rules');
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', padding: 24 }}>
        <style>{styles}</style>
        <div style={{ background: '#0f172a', borderRadius: 20, border: '1px solid rgba(99,102,241,0.25)', padding: '40px 36px', maxWidth: 420, width: '100%', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 44, marginBottom: 8, filter: 'drop-shadow(0 0 16px rgba(99,102,241,0.6))' }}>⬡</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', margin: '0 0 6px' }}>Verify Your Identity</h2>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
              Enter your email and the access password sent in your invite email.
            </p>
          </div>

          {session?.job_title && (
            <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, padding: '8px 14px', marginBottom: 20, fontSize: 13, color: '#a5b4fc', textAlign: 'center' }}>
              <ClipboardList size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> {session.job_title}
            </div>
          )}

          <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Email Address</label>
              <input
                name="email"
                type="email"
                required
                placeholder="Enter the email you applied with"
                style={{ width: '100%', padding: '11px 14px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Access Password</label>
              <input
                name="password"
                type="text"
                required
                placeholder="6-character code from your email"
                maxLength={10}
                style={{ width: '100%', padding: '11px 14px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase', letterSpacing: 2, fontFamily: 'monospace' }}
              />
            </div>

            {error && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#f87171' }}>
                <AlertTriangle size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{ marginTop: 4, padding: 14, background: loading ? '#1e293b' : 'linear-gradient(135deg, #4f46e5, #6366f1)', color: loading ? '#475569' : '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: loading ? 'none' : '0 4px 20px rgba(99,102,241,0.4)' }}
            >
              {loading
                ? <><Hourglass size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Verifying...</>
                : <>Verify & Continue <ArrowRight size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /></>}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: 11, color: '#334155', marginTop: 16 }}>
            <AlarmClock size={11} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-1px' }} /> This link is valid for 12 hours from when your invite was sent
          </p>
        </div>
      </div>
    );
  }

  // ── Loading ──
  if (phase === 'loading') return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
      <style>{styles}</style>
      {error ? (
        <div style={{ textAlign: 'center', color: '#f87171' }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><AlertTriangle size={32} strokeWidth={1.6} /></div>
          <div style={{ fontSize: 15 }}>{error}</div>
          <button onClick={() => navigate('/portal')} style={{ marginTop: 16, padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}><ArrowLeft size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Back to Jobs</button>
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 40, marginBottom: 12, filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.5))' }}>⬡</div>
          <div style={{ fontSize: 15 }}>Loading your interview...</div>
        </div>
      )}
    </div>
  );

  // ── Rules ──
  if (phase === 'rules') return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', padding: 24 }}>
      <style>{styles}</style>
      <div style={{ background: '#0f172a', borderRadius: 20, border: '1px solid rgba(99,102,241,0.25)', padding: '40px 36px', maxWidth: 560, width: '100%', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 8, filter: 'drop-shadow(0 0 16px rgba(99,102,241,0.6))' }}>⬡</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', margin: '0 0 6px' }}>Interview Guidelines</h2>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{session?.candidate_name} — {session?.job_title}</p>
        </div>
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 10, padding: 14, marginBottom: 22 }}>
          <strong style={{ fontSize: 13, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={2} /> This interview is proctored</strong>
          <p style={{ fontSize: 12, color: '#d97706', margin: '4px 0 0' }}>Camera, microphone, and screen activity are recorded throughout.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            [Camera, 'Keep your face visible in the camera at all times'],
            [VolumeX, 'Find a quiet, distraction-free environment'],
            [Monitor, 'Do not switch tabs or open other applications'],
            [Mic, 'Speak your answers — typing is not allowed'],
            [Timer, `Answer ${session?.questions_total || 0} questions at your own pace`],
            [MessageSquare, 'Say “skip” to skip a question — it affects your score'],
          ].map(([Icon, text], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: '#cbd5e1', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}><Icon size={18} strokeWidth={1.9} /></span>
              <span>{text}</span>
            </div>
          ))}
        </div>
        <button
          onClick={() => setPhase('permissions')}
          style={{ width: '100%', marginTop: 24, padding: 14, background: 'linear-gradient(135deg, #4f46e5, #6366f1)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(99,102,241,0.4)' }}
        >
          I Understand, Continue <ArrowRight size={15} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} />
        </button>
        <p style={{ textAlign: 'center', fontSize: 12, color: '#475569', marginTop: 10 }}>By continuing you consent to recording and proctoring.</p>
      </div>
    </div>
  );

  // ── Permissions ──
  if (phase === 'permissions') return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui', padding: 24 }}>
      <style>{styles}</style>
      <div style={{ background: '#0f172a', borderRadius: 20, border: '1px solid rgba(99,102,241,0.25)', padding: '40px 36px', maxWidth: 460, width: '100%', textAlign: 'center', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', color: '#818cf8' }}><Video size={48} strokeWidth={1.5} /></div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>Allow Access to Proceed</h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
          {resumePending
            ? 'Your progress was restored. Reconnect to continue from where you left off.'
            : 'We need camera, microphone, and screen access to conduct the interview.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, textAlign: 'left' }}>
          {[[Camera, 'Camera', 'Video recording & face detection'], [Mic, 'Microphone', 'Speech-to-text transcription'], [Monitor, 'Screen', 'Activity monitoring']].map(([Icon, title, desc]) => (
            <div key={title} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ display: 'flex', alignItems: 'center', color: '#818cf8' }}><Icon size={22} strokeWidth={1.8} /></span>
              <div><div style={{ fontWeight: 600, fontSize: 14, color: '#e2e8f0' }}>{title}</div><div style={{ fontSize: 12, color: '#64748b' }}>{desc}</div></div>
            </div>
          ))}
        </div>
        <button
          onClick={setupMedia}
          style={{ width: '100%', padding: 14, background: 'linear-gradient(135deg, #4f46e5, #6366f1)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(99,102,241,0.4)' }}
        >
          {resumePending ? 'Reconnect & Continue Interview' : 'Grant Access & Start Interview'}
        </button>
      </div>
    </div>
  );

  // ── Uploading ──
  if (phase === 'uploading') return (
    <div style={{ minHeight: '100vh', background: '#080e1c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
      <style>{styles}</style>
      <div style={{ textAlign: 'center', color: '#94a3b8' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center', color: '#818cf8' }}><Upload size={40} strokeWidth={1.5} /></div>
        <h2 style={{ color: '#f1f5f9', fontWeight: 700 }}>Finalizing your interview...</h2>
        <p style={{ fontSize: 14 }}>Uploading recordings and submitting your interview for evaluation.</p>
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #1e293b', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      </div>
    </div>
  );

  // ── Results ──
  if (phase === 'results' && results) {
    const answered = questionStates.filter(s => s === 'answered').length;
    const skipped = questionStates.filter(s => s === 'skipped').length;
    return (
      <div style={{ minHeight: '100vh', background: '#080e1c', fontFamily: 'system-ui', padding: '40px 24px' }}>
        <style>{styles}</style>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 36, marginBottom: 8, filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.5))' }}>⬡</div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>Interview Complete</h1>
            <p style={{ color: '#64748b', fontSize: 15, marginTop: 6 }}>{session?.candidate_name} · {session?.job_title}</p>
          </div>

          <div style={{ background: '#0f172a', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', padding: 32, marginBottom: 20, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, color: '#34d399', display: 'flex', justifyContent: 'center' }}><CheckCircle2 size={48} strokeWidth={1.6} /></div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>
              Interview Submitted
            </div>
            <div style={{ fontSize: 14, color: '#94a3b8', maxWidth: 440, margin: '0 auto' }}>
              Your answers have been saved successfully. Evaluation will continue in the background and the hiring team will review the results.
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 20, flexWrap: 'wrap' }}>
              {[['Answered', answered], ['Skipped', skipped], ['Time Taken', formatTime(results.time_taken_seconds || 0)], ['Screenshots', proctoring.screenshots.length]].map(([label, val]) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9' }}>{val}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'linear-gradient(135deg, #312e81, #4f46e5)', borderRadius: 16, padding: 28, color: '#fff', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Handshake size={28} strokeWidth={1.7} /></div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Thank you, {session?.candidate_name}!</h3>
            <p style={{ margin: 0, opacity: 0.85, fontSize: 14 }}>Your interview has been submitted. The hiring team will review your responses and reach out shortly.</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button onClick={() => navigate('/portal')} style={{ padding: '10px 24px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 14, cursor: 'pointer', color: '#94a3b8' }}><ArrowLeft size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Browse More Jobs</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Interviewing ──────────────────────────────────────────────────────────────
  const maxPossible = questions.reduce((s, q) => s + (q.max_marks || 0), 0);
  const answeredCount = questionStates.filter(s => s === 'answered').length;
  const skippedCount = questionStates.filter(s => s === 'skipped').length;
  const attemptedCount = answeredCount + skippedCount;
  const lastQuestion = [...messages].filter(m => m.isQuestion).slice(-1)[0];

  const statusLabel = { speaking: 'Speaking...', thinking: 'Thinking...', listening: 'Listening...', waiting: 'Waiting for your answer' }[aiStatus];
  const statusColor = { speaking: '#a5b4fc', thinking: '#fbbf24', listening: '#34d399', waiting: '#64748b' }[aiStatus];

  const difficultyStyle = (d) => ({
    Advanced: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: 'Advanced' },
    Intermediate: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', label: 'Intermediate' },
    Basic: { bg: 'rgba(34,197,94,0.15)', color: '#34d399', label: 'Basic' },
  }[d] || null);

  return (
    <div style={{ height: '100vh', background: '#080e1c', fontFamily: 'system-ui', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{styles}</style>

      {/* ── Top bar ── */}
      <div style={{ height: 52, background: 'rgba(15,23,42,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', paddingInline: 20, gap: 16, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18, filter: 'drop-shadow(0 0 6px rgba(99,102,241,0.6))' }}>⬡</span>
          <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 14 }}>{session?.job_title || 'Interview'}</span>
        </div>
        <div style={{ color: '#334155', fontSize: 16 }}>·</div>
        <span style={{ color: '#64748b', fontSize: 13 }}>{session?.candidate_name}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Q {Math.min(currentQIdx + 1, questions.length)}/{questions.length}</span>
            <div style={{ width: 80, height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg,#4f46e5,#818cf8)', width: `${Math.round((attemptedCount / Math.max(questions.length, 1)) * 100)}%`, transition: 'width 0.4s' }} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#94a3b8' }}>
            <span style={{ display: 'flex', alignItems: 'center' }}><Timer size={13} strokeWidth={2} /></span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)}</span>
          </div>
          <div style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#a5b4fc' }}>
            {answeredCount} done · {skippedCount} skipped
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.5s ease infinite' }} />
            <span style={{ fontSize: 11, color: '#64748b' }}>REC</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 32px 0', position: 'relative', gap: 14, overflow: 'hidden' }}>

        {/* Ambient glow */}
        <div style={{ position: 'absolute', top: '15%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, background: 'radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)', pointerEvents: 'none', borderRadius: '50%' }} />

        {/* Avatar */}
        <AiAvatar speaking={isAiSpeaking} thinking={loading} size={156} photoUrl={session?.interviewer_photo} name={session?.interviewer_name || 'Alex'} />

        {/* AI name + status */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#e2e8f0' }}>{session?.interviewer_name || 'Alex'}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>AI Interviewer</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, boxShadow: `0 0 6px ${statusColor}`, animation: aiStatus === 'listening' || aiStatus === 'speaking' ? 'pulse 1.2s ease infinite' : 'none' }} />
            <span style={{ fontSize: 12, color: statusColor, fontWeight: 500 }}>{statusLabel}</span>
          </div>
        </div>


        {/* Current question + spoken answer */}
        {lastQuestion && (
          <div style={{ maxWidth: 620, width: '100%', display: 'flex', flexDirection: 'column', gap: 10, animation: 'slideUp 0.4s ease' }}>
            <div key={lastQuestion.qIdx} style={{
              background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(129,140,248,0.3)',
              borderRadius: 16, padding: '16px 20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Question {(lastQuestion.qIdx || 0) + 1}
                </span>
                {lastQuestion.difficulty && difficultyStyle(lastQuestion.difficulty) && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    background: difficultyStyle(lastQuestion.difficulty).bg,
                    color: difficultyStyle(lastQuestion.difficulty).color,
                  }}>
                    {difficultyStyle(lastQuestion.difficulty).label}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 15, color: '#e2e8f0', lineHeight: 1.6, fontWeight: 500 }}>
                {lastQuestion.content}
              </div>
            </div>

            <div style={{
              background: transcript ? 'rgba(16,185,129,0.1)' : '#0f172a',
              border: `1px solid ${
                questionTimer <= 10 ? 'rgba(239,68,68,0.55)'
                : questionTimer <= 30 ? 'rgba(245,158,11,0.4)'
                : transcript ? 'rgba(52,211,153,0.35)'
                : 'rgba(255,255,255,0.07)'
              }`,
              borderRadius: 16, padding: '14px 20px', minHeight: 96,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#34d399', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Your answer
                </span>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '2px 8px', borderRadius: 20,
                  background: questionTimer <= 10 ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
                }}>
                  <Hourglass size={11} strokeWidth={2.2} />
                  <span style={{
                    fontSize: 12, fontWeight: 800, fontFamily: 'monospace',
                    color: questionTimer <= 10 ? '#f87171' : '#94a3b8',
                  }}>{formatTime(questionTimer)}</span>
                </div>
              </div>
              <div style={{ fontSize: 14, color: transcript ? '#e2e8f0' : '#64748b', lineHeight: 1.6, minHeight: 44 }}>
                {transcript || (isAiSpeaking ? 'Answer will appear here after you speak.' : 'Take your time. Pause is OK. Say “that’s all” when you finish, or “skip”.')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8 }}>
                <div style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: isRecording ? '#22c55e' : '#1e293b',
                  boxShadow: isRecording ? '0 0 8px #22c55e' : 'none',
                  animation: isRecording ? 'pulse 1s infinite' : 'none',
                }} />
                <MicWave level={micLevel} active={isRecording} />
                <span style={{ fontSize: 10, color: isRecording ? '#22c55e' : '#475569' }}>
                  {isRecording ? 'Listening' : 'Mic idle'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── PiP Camera (fixed corner) ── */}
      <div style={{ position: 'fixed', top: 68, right: 16, width: 140, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 20px rgba(0,0,0,0.5)', background: '#000', zIndex: 50 }}>
        <video ref={videoRef} autoPlay muted playsInline
          style={{ width: '100%', display: 'block', transform: 'scaleX(-1)', aspectRatio: '4/3', objectFit: 'cover' }}
        />
        {/* Status overlays */}
        <div style={{ position: 'absolute', top: 4, left: 4, display: 'flex', gap: 3 }}>
          {isRecording && (
            <div style={{ background: 'rgba(220,38,38,0.9)', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <div style={{ width: 4, height: 4, background: '#fff', borderRadius: '50%', animation: 'pulse 1s infinite' }} /> REC
            </div>
          )}
        </div>
        <div style={{ position: 'absolute', top: 4, right: 4, background: proctoring.faceDetected ? 'rgba(5,150,105,0.9)' : 'rgba(220,38,38,0.9)', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 3 }}>
          {proctoring.faceDetected
            ? <Check size={10} strokeWidth={3} style={{ display: 'block' }} />
            : <AlertTriangle size={10} strokeWidth={2.5} style={{ display: 'block' }} />}
        </div>
        <div style={{ background: 'rgba(0,0,0,0.6)', padding: '3px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>{session?.candidate_name?.split(' ')[0]}</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <span style={{ display: 'flex', alignItems: 'center', color: cameraActive ? '#34d399' : '#f87171' }}><Camera size={10} strokeWidth={2.4} /></span>
            <span style={{ display: 'flex', alignItems: 'center', color: micActive ? '#34d399' : '#f87171' }}><Mic size={10} strokeWidth={2.4} /></span>
          </div>
        </div>
        {!cameraActive && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(8,14,28,0.85)', color: '#475569', fontSize: 10, borderRadius: 10 }}>
            Starting…
          </div>
        )}
      </div>
    </div>
  );
}
